import { Button, Card, Col, Input, Row, Statistic, Table } from 'antd';
import { DataTable, RangeDatePicker } from '~/components/common';
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaChartLine,
  FaHistory,
  FaMoneyBillWave,
  FaSearch,
  FaUndoAlt,
  FaUsers,
  FaWallet,
} from 'react-icons/fa';
import React, { Fragment, useEffect, useReducer, useState } from 'react';
import { baseUrl } from '~/utils/helper';

import { HistoryRefundGrid } from '~/data/dummy';
import { InputAntd } from '.';
import SETTINGS from '~/setting.json';
import axios from 'axios';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useStateContext } from '~/contexts/ContextProvider';

const reducer = (state, { action, payload }) => {
  switch (action) {
    case 'changeDate':
      return {
        ...state,
        startDate: payload.startDate + ' 00:00:00',
        endDate: payload.endDate + ' 23:59:59',
      };
    case 'username':
      return { ...state, username: payload };
    case 'page':
      return { ...state, page: payload };
    default:
      return state;
  }
};

const getRefundHistory = async (params) => {
  try {
    const { data } = await axios.get(baseUrl + '/api/daily/refund/history', {
      params,
      headers: {
        'x-access-token': sessionStorage.getItem('auth'),
        'Access-Control-Allow-Origin': '*',
      },
    });
    if (data.status) {
      return data.data;
    }
    return {};
  } catch (error) {
    console.log(error);
  }
};

export default function Refund({ title }) {
  const { formatMoney, ref } = useStateContext();
  const { t } = useTranslation('report');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [params, dispatch] = useReducer(reducer, {
    startDate: dayjs().add(-7, 'd').format('YYYY-MM-DD') + ' 00:00:00',
    endDate: dayjs().format('YYYY-MM-DD') + ' 23:59:59',
    username: '',
    page: 1,
  });

  document.title = title;

  useEffect(() => {
    initData();
  }, []);

  const initData = async () => {
    setLoading(true);
    const data = await getRefundHistory(params);
    setData(data);
    setLoading(false);
  };

  const handleSearch = async () => {
    setLoading(true);
    const data = await getRefundHistory({ ...params, page: 1 });
    setData(data);
    setLoading(false);
  };

  const handleChangeDate = (v) => {
    dispatch({
      action: 'changeDate',
      payload: { startDate: v[0], endDate: v[1] },
    });
  };

  const handleChangePage = (page) => {
    getRefundHistory({ ...page, page: page });
    dispatch({ action: 'page', payload: page });
  };

  const historyRefundGrid = HistoryRefundGrid();

  // Calculate refund percentage
  const refundPercentage = data?.totalBet
    ? ((data?.totalRefund / data?.totalBet) * 100).toFixed(2)
    : 0;

  return (
    <div className="min-h-screen p-1 md:p-6 bg-gradient-to-br from-slate-50 via-orange-50 to-amber-100">
      {/* Header Section */}
      <div className="p-2 mb-4 border shadow-xl md:mb-8 md:p-6 rounded-2xl bg-white/90 backdrop-blur-2xl border-white/20">
        <div className="flex flex-col justify-between gap-3 md:flex-row">
          {/* Title */}
          <div className="">
            <h1 className="flex items-center text-2xl font-bold text-center text-transparent bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text md:text-left md:text-3xl md:mb-2">
              <FaUndoAlt className="inline mr-3 text-orange-500" />
              {t('lich_su_hoan_tra')}
            </h1>
            <p className="hidden text-gray-600 md:block">{t('ql_va_td_lsht')}</p>
          </div>

          {/* Controls Row */}
          <div className="flex flex-col gap-2 lg:flex-row md:gap-4 lg:items-end">
            {/* Date Range */}
            <div className="flex flex-col gap-2">
              <label className="items-center hidden gap-2 text-sm font-medium text-gray-700 md:flex">
                <FaCalendarAlt className="text-blue-500" />
                {t('khoang_thoi_gian')}
              </label>
              <RangeDatePicker
                onChange={handleChangeDate}
                className="w-full h-10 border-2 rounded-lg md:h-12 border-orange-500/20 bg-white/90"
              />
            </div>

            {/* Username Search */}
            <div className="flex justify-between gap-2 md:flex-col md:justify-center">
              <label className="items-center hidden gap-2 text-sm font-medium text-gray-700 md:flex">
                <FaUsers className="text-purple-500" />
                {t('nguoichoi')}
              </label>
              <Input
                value={params.username}
                onChange={(e) => dispatch({ action: 'username', payload: e.target.value })}
                placeholder={t('nhap_ten_dang_nhap')}
                className="md:w-[220px] w-3/5 rounded-lg md:h-12 h-10"
                allowClear
              />
              <button
                onClick={handleSearch}
                className="flex items-center justify-center h-10 gap-2 px-3 py-3 font-semibold text-white transition-all duration-300 border-none shadow-lg md:hidden md:h-12 rounded-xl bg-gradient-to-r from-orange-500 to-amber-600 hover:scale-105 hover:shadow-xl">
                <FaSearch size={16} />
                {t('common:search')}
              </button>
            </div>

            {/* Search Button */}
            <button
              onClick={handleSearch}
              className="items-center justify-center hidden h-10 gap-2 px-6 py-3 font-semibold text-white transition-all duration-300 border-none shadow-lg md:flex md:h-12 rounded-xl bg-gradient-to-r from-orange-500 to-amber-600 hover:scale-105 hover:shadow-xl">
              <FaSearch size={16} />
              {t('common:search')}
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} className="mb-4 text-2xl md:mb-8">
        <Col xs={12} sm={12} lg={12}>
          <Card className="text-center bg-gradient-to-r from-blue-500 to-blue-600 border-none rounded-2xl text-white hover:scale-105 transition-all duration-300 md:[&_.ant-card-body]:!p-[24px] [&_.ant-card-body]:!p-[12px]">
            <Statistic
              title={
                <span className="flex items-center justify-center gap-2 text-white/90">
                  <FaMoneyBillWave /> {t('tongcuoc')}
                </span>
              }
              value={data?.totalBet || 0}
              formatter={(value) => formatMoney(value)}
              valueStyle={{
                color: 'white',
                fontWeight: 'bold',
                fontSize: '18px',
              }}
            />
          </Card>
        </Col>

        <Col xs={12} sm={12} lg={12}>
          <Card className="text-center bg-gradient-to-r from-green-500 to-emerald-600 border-none rounded-2xl text-white hover:scale-105 transition-all duration-300 md:[&_.ant-card-body]:!p-[24px] [&_.ant-card-body]:!p-[12px]">
            <Statistic
              title={
                <span className="flex items-center justify-center gap-2 text-white/90">
                  <FaArrowLeft /> {t('hoantra')}
                </span>
              }
              value={data?.totalRefund || 0}
              formatter={(value) => formatMoney(value)}
              valueStyle={{
                color: 'white',
                fontWeight: 'bold',
                fontSize: '18px',
              }}
            />
          </Card>
        </Col>

        {/* <Col xs={24} sm={24} lg={8}>
          <Card className="text-center text-white transition-all duration-300 border-none bg-gradient-to-r from-purple-500 to-indigo-600 rounded-2xl hover:scale-105">
            <Statistic
              title={
                <span className="flex items-center justify-center gap-2 text-white/90">
                  <FaChartLine /> Tỷ lệ hoàn trả
                </span>
              }
              value={refundPercentage}
              suffix="%"
              valueStyle={{
                color: "white",
                fontWeight: "bold",
                fontSize: "18px",
              }}
            />
          </Card>
        </Col> */}
      </Row>

      {/* Data Table Section */}
      <div className="p-2 border shadow-xl md:p-6 rounded-2xl bg-white/90 backdrop-blur-2xl border-white/20">
        <div className="custom-refund-table">
          <Table
            columns={historyRefundGrid}
            loading={loading}
            dataSource={data?.data}
            sticky={true}
            pagination={{
              total: data?.total,
              showSizeChanger: false,
              onChange: (page) => handleChangePage(page),
            }}
            scroll={{ x: 'max-content' }}
          />
        </div>
      </div>

      <style jsx>{`
        .custom-refund-table .ant-table-thead > tr > th {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%) !important;
          border-bottom: 2px solid #e2e8f0 !important;
          font-weight: 600 !important;
          color: #374151 !important;
          padding: 16px !important;
          text-align: center !important;
        }

        .custom-refund-table .ant-table-tbody > tr > td {
          padding: 12px 16px !important;
          border-bottom: 1px solid #f1f5f9 !important;
          text-align: center !important;
        }

        .custom-refund-table .ant-table-tbody > tr:nth-child(even) {
          background-color: #f9fafb !important;
        }

        .custom-refund-table .ant-table-tbody > tr:hover {
          background-color: #fef3e2 !important;
        }

        .ant-pagination .ant-pagination-item {
          border-radius: 8px !important;
          border: 1px solid #e2e8f0 !important;
        }

        .ant-pagination .ant-pagination-item-active {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
          border-color: transparent !important;
        }

        .ant-pagination .ant-pagination-item-active a {
          color: white !important;
        }

        .ant-table-summary tr td {
          border-top: 2px solid #d97706 !important;
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
          color: white !important;
          font-weight: bold !important;
        }

        /* Custom scrollbar */
        .custom-refund-table ::-webkit-scrollbar {
          height: 8px;
        }

        .custom-refund-table ::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }

        .custom-refund-table ::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          border-radius: 4px;
        }

        .custom-refund-table ::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
        }

        /* Mobile responsive adjustments */
        @media (max-width: 768px) {
          .custom-refund-table .ant-table-thead > tr > th,
          .custom-refund-table .ant-table-tbody > tr > td {
            padding: 8px 12px !important;
            font-size: 14px !important;
          }
        }
      `}</style>
    </div>
  );
}
